import { useEffect, useState } from 'react';

const MOBILE_QUERY = '(max-width: 767px)';

/**
 * 좌우 두 칸이 <b>위아래로 쌓이는</b> 폭인가.
 *
 * Tailwind 의 `lg`(1024px) 바로 아래다 — 목록·상세를 나란히 두는 화면들이 그 지점에서 한 줄로
 * 무너진다(`lg:grid-cols-[...]`). 무너진 뒤에는 «고르면 <b>아래</b>에 붙는» 배치가 되어,
 * 고른 사람은 자기가 무엇을 눌렀는지 화면에서 확인하지 못한 채 스크롤부터 해야 한다.
 * 그 자리를 아는 화면들이 이 값으로 «접기·자동 이동» 을 켠다.
 */
const STACKED_QUERY = '(max-width: 1023px)';

/**
 * 미디어 쿼리 하나를 구독한다.
 *
 * <b>CSS 로 되는 일은 CSS 로 한다</b> — 이 훅은 «보이고 안 보이고» 가 아니라 <b>행동</b>이
 * 갈릴 때만 쓴다(고르면 목록을 접을 것인가, Enter 를 저장으로 볼 것인가). 단순히 감추는 것은
 * `hidden lg:block` 이 낫다: 훅으로 하면 화면 폭이 바뀔 때마다 React 가 다시 그린다.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);

  useEffect(() => {
    const mediaQuery = window.matchMedia(query);
    const update = (event: MediaQueryListEvent) => setMatches(event.matches);

    // 훅이 붙는 사이에 폭이 바뀌었을 수 있다. 구독 직전에 한 번 맞춘다.
    setMatches(mediaQuery.matches);
    mediaQuery.addEventListener('change', update);
    return () => mediaQuery.removeEventListener('change', update);
  }, [query]);

  return matches;
}

/**
 * 좁은 화면인가.
 *
 * 업무 입력칸에서 Enter 를 "저장"으로 쓸지 판단하는 데 쓴다. 모바일 키보드의 Enter 는 줄바꿈이라
 * 저장으로 가로채면 여러 줄을 아예 못 쓴다.
 */
export function useIsMobile(): boolean {
  return useMediaQuery(MOBILE_QUERY);
}

/** 목록과 상세가 위아래로 쌓이는 폭인가(모바일·아이패드 세로). */
export function useStackedLayout(): boolean {
  return useMediaQuery(STACKED_QUERY);
}
